import React, { FC } from 'react'

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from '@mui/material'

interface DeleteViewModalProps {
  open: boolean
  viewLabel: string
  isSaving?: boolean
  onConfirm: () => void
  onCancel: () => void
}

/**
 * VERA-300 — confirm before deleting a custom link.
 *
 * Deletion is irreversible and immediately revokes access for everyone holding the
 * link, so the confirm button is filled red (a transient, already-committed surface
 * per the destructive-action convention) rather than the neutral primary used by the
 * reversible "Disable view".
 *
 * Only custom links reach this. The public view has no delete.
 */
export const DeleteViewModal: FC<DeleteViewModalProps> = ({
  open,
  viewLabel,
  isSaving,
  onConfirm,
  onCancel,
}) => {
  const theme = useTheme()

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth='sm'
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px', p: 0 } }}
    >
      <DialogTitle sx={{ fontSize: 20, fontWeight: 400, pb: 1, textTransform: 'none' }}>
        Delete view
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        <Typography
          sx={{ fontSize: 16, fontWeight: 400, color: theme.palette.text.secondary, lineHeight: 1.5 }}
        >
          Are you sure you want to delete {viewLabel}? This cannot be undone. Anyone
          holding this link will lose access immediately.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 2 }}>
        <Button
          fullWidth
          variant='contained'
          disabled={isSaving}
          onClick={onConfirm}
          sx={{
            backgroundColor: theme.palette.error.main,
            color: theme.palette.common.white,
            borderRadius: '16px',
            textTransform: 'none',
            py: 1.5,
            '&:hover': { backgroundColor: theme.palette.error.dark },
          }}
        >
          Delete
        </Button>
        <Button
          fullWidth
          variant='outlined'
          disabled={isSaving}
          onClick={onCancel}
          sx={{
            borderColor: theme.palette.grey[300],
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.common.white,
            borderRadius: '16px',
            textTransform: 'none',
            py: 1.5,
            ml: '0 !important',
            '&:hover': { backgroundColor: theme.palette.grey[50] },
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}
